import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import {
  beginRealtimeTrackSession,
  bootstrapMemberTrackDrawing,
  mergeRealtimeTrackPoint,
  clearMemberPositionWatchers,
  endTrackSession,
  saveTrackPointToDatabase,
  drawRealtimeTrackLineOnMap,
  removeRealtimeTrackLineFromMap,
  getRealtimeTrackLineId,
  safeGetLayer as safeGetLayerUtil
} from '../utils/dashboard';
import {
  loadPersistedRealtimeTracks,
  savePersistedRealtimeTracks,
  removePersistedMemberRealtimeTrack
} from '../utils/dashboard/trackPersistenceHelpers';
import { dashboardDebug } from '../utils/dashboard/dashboardDebug';
import { sanitizeTrackPoints } from '../utils/dashboard/trackSegmentHelpers';

/**
 * 指挥页实时轨迹绘制（手动启动；刷新不丢点；停止后保留已绘线）
 * @param {number} [offlineGraceMinutes=30] 持续离线超过该分钟数后自动停止续画（线条保留）
 */
export default function useDashboardRealtimeTracks({
  getMap,
  members,
  membersRef,
  refreshInterval,
  offlineGraceMinutes = 30,
  trackColors = {}
}) {
  const initialPersisted = useRef(null);
  if (!initialPersisted.current) {
    initialPersisted.current = loadPersistedRealtimeTracks();
  }

  const [realTimeTrackDrawing, setRealTimeTrackDrawing] = useState(
    () => new Set(initialPersisted.current.drawing || [])
  );
  const [receivingTrackData, setReceivingTrackData] = useState(new Set());
  const [realTimeTrackData, setRealTimeTrackData] = useState(
    () => initialPersisted.current.trackData || {}
  );
  // 实时轨迹线显隐（与是否续画独立；默认有数据即显示）
  const [showRealtimeTracks, setShowRealtimeTracks] = useState({});
  const [trackDrawingLines, setTrackDrawingLines] = useState({});
  const restoredRef = useRef(false);
  const resumedDrawingRef = useRef(new Set());
  const graceNotifiedRef = useRef(new Set());

  const realTimeTrackDataRef = useRef(realTimeTrackData);
  const trackDrawingLinesRef = useRef(trackDrawingLines);
  const realTimeTrackDrawingRef = useRef(realTimeTrackDrawing);
  const showRealtimeTracksRef = useRef(showRealtimeTracks);
  const offlineGraceMinutesRef = useRef(offlineGraceMinutes);
  const stopTrackFeaturesRef = useRef(null);
  const trackColorsRef = useRef(trackColors);
  useEffect(() => { realTimeTrackDataRef.current = realTimeTrackData; }, [realTimeTrackData]);
  useEffect(() => { trackDrawingLinesRef.current = trackDrawingLines; }, [trackDrawingLines]);
  useEffect(() => { realTimeTrackDrawingRef.current = realTimeTrackDrawing; }, [realTimeTrackDrawing]);
  useEffect(() => { showRealtimeTracksRef.current = showRealtimeTracks; }, [showRealtimeTracks]);
  useEffect(() => { offlineGraceMinutesRef.current = offlineGraceMinutes; }, [offlineGraceMinutes]);
  useEffect(() => { trackColorsRef.current = trackColors; }, [trackColors]);

  // 持久化：绘制中名单 + 轨迹点（页面刷新后可恢复）
  useEffect(() => {
    savePersistedRealtimeTracks({
      drawing: [...realTimeTrackDrawing],
      trackData: realTimeTrackData
    });
  }, [realTimeTrackDrawing, realTimeTrackData]);

  const safeGetLayer = useCallback((layerId) => {
    return safeGetLayerUtil(getMap?.(), layerId);
  }, [getMap]);

  const isRealtimeTrackVisible = useCallback((memberName) => {
    return showRealtimeTracksRef.current[memberName] !== false;
  }, []);

  const drawTrackLine = useCallback((memberName) => {
    const map = getMap?.();
    if (!map || !map.isStyleLoaded()) {
      setTimeout(() => drawTrackLine(memberName), 200);
      return;
    }
    const trackPoints = realTimeTrackDataRef.current[memberName];
    if (!trackPoints || trackPoints.length === 0) return;

    // 显隐关闭时只移除图层，保留点位数据
    if (!isRealtimeTrackVisible(memberName)) {
      const lineId = trackDrawingLinesRef.current[memberName] || getRealtimeTrackLineId(memberName);
      removeRealtimeTrackLineFromMap(map, memberName, lineId);
      setTrackDrawingLines((prev) => {
        if (!prev[memberName]) return prev;
        const next = { ...prev };
        delete next[memberName];
        return next;
      });
      return;
    }

    const memberList = membersRef?.current || members || [];
    const member = (Array.isArray(memberList) ? memberList : []).find((m) => m && m.name === memberName);
    const lineId = drawRealtimeTrackLineOnMap(map, memberName, trackPoints, {
      trackColors: trackColorsRef.current || {},
      deviceUniqueId: member?.device_unique_id || member?.deviceUniqueId,
      members: memberList,
      onLineCreated: (id) => {
        setTrackDrawingLines((prev) => ({ ...prev, [memberName]: id }));
      }
    });
    if (!lineId) {
      setTimeout(() => drawTrackLine(memberName), 200);
    } else {
      setTimeout(() => {
        const currentMap = getMap?.();
        if (currentMap && safeGetLayer(lineId)) {
          currentMap.triggerRepaint();
        }
      }, 100);
    }
  }, [getMap, safeGetLayer, isRealtimeTrackVisible, members, membersRef]);

  const removeTrackDrawingLine = useCallback((memberName) => {
    const lineId = trackDrawingLinesRef.current[memberName] || getRealtimeTrackLineId(memberName);
    removeRealtimeTrackLineFromMap(getMap?.(), memberName, lineId);
    setTrackDrawingLines((prev) => {
      const next = { ...prev };
      delete next[memberName];
      return next;
    });
  }, [getMap]);

  const startTrackDrawing = useCallback(async (member, sessionId = null) => {
    await bootstrapMemberTrackDrawing({
      member,
      sessionId,
      getCurrentMember: () => members.find((m) => m.name === member.name),
      saveInitialPoint: saveTrackPointToDatabase,
      onInitialPoint: (name, initialPoint) => {
        setRealTimeTrackData((prev) => {
          const existing = prev[name] || [];
          // 已有持久化/续画点时不覆盖
          if (existing.length > 0) return prev;
          return { ...prev, [name]: [initialPoint] };
        });
      },
      onDrawLine: drawTrackLine,
      refreshIntervalMs: refreshInterval,
      getMember: () => (membersRef.current || members).find((m) => m.name === member.name),
      onPointAdded: ({ member: currentMember, sessionId: sid }) => {
        setRealTimeTrackData((prev) => {
          const { nextState, added, newPoint } = mergeRealtimeTrackPoint(prev, member.name, currentMember);
          if (!added) return prev;
          if (sid) {
            saveTrackPointToDatabase(member, newPoint, sid).catch((error) => {
              console.error('保存轨迹点到数据库失败:', error);
            });
          }
          setTimeout(() => drawTrackLine(member.name), 50);
          return nextState;
        });
      }
    });
  }, [members, membersRef, refreshInterval, drawTrackLine]);

  const setRealtimeTrackVisible = useCallback((memberName, visible) => {
    setShowRealtimeTracks((prev) => ({ ...prev, [memberName]: !!visible }));
    setTimeout(() => {
      if (visible) drawTrackLine(memberName);
      else {
        const lineId = trackDrawingLinesRef.current[memberName] || getRealtimeTrackLineId(memberName);
        removeRealtimeTrackLineFromMap(getMap?.(), memberName, lineId);
        setTrackDrawingLines((prev) => {
          if (!prev[memberName]) return prev;
          const next = { ...prev };
          delete next[memberName];
          return next;
        });
      }
    }, 30);
  }, [drawTrackLine, getMap]);

  const hideRealtimeTrack = useCallback((memberName) => {
    setRealtimeTrackVisible(memberName, false);
  }, [setRealtimeTrackVisible]);

  /** 是否已超过离线停画宽限（无 last_seen 时不视为超限，与停画 tick 一致） */
  const isPastOfflineGrace = useCallback((memberName) => {
    const graceMin = Math.max(1, Number(offlineGraceMinutesRef.current) || 30);
    const graceMs = graceMin * 60 * 1000;
    const source = membersRef.current || members;
    const member = (source || []).find((m) => m.name === memberName);
    const lastSeen = member?.last_seen ? new Date(member.last_seen).getTime() : NaN;
    if (!Number.isFinite(lastSeen)) return false;
    return Date.now() - lastSeen >= graceMs;
  }, [members, membersRef]);

  /**
   * 开启实时续画。
   * @param {string} memberName
   * @param {{ auto?: boolean }} [options] auto=true：任务确保/自动恢复；仍超宽限或本轮已提示则拒绝开画且不清 graceNotified
   */
  const startRealTimeTrackDrawing = useCallback(async (memberName, options = {}) => {
    const isAuto = options?.auto === true;
    const pastGrace = isPastOfflineGrace(memberName);
    if (isAuto && (graceNotifiedRef.current.has(memberName) || pastGrace)) {
      dashboardDebug('跳过自动开画（离线超限或本轮已停画）:', memberName);
      setShowRealtimeTracks((prev) => ({ ...prev, [memberName]: true }));
      if ((realTimeTrackDataRef.current[memberName] || []).length > 0) {
        setTimeout(() => drawTrackLine(memberName), 50);
      }
      return;
    }
    console.log('开启实时轨迹绘制:', memberName);
    // 仅在已回到宽限内时清除「已提示」；手动开画也允许清，便于用户强制续画
    if (!pastGrace || !isAuto) {
      graceNotifiedRef.current.delete(memberName);
    }
    setShowRealtimeTracks((prev) => ({ ...prev, [memberName]: true }));
    // 避免重复启动 watcher
    if (realTimeTrackDrawingRef.current.has(memberName) && resumedDrawingRef.current.has(memberName)) {
      if ((realTimeTrackDataRef.current[memberName] || []).length > 0) {
        setTimeout(() => drawTrackLine(memberName), 50);
      }
      return;
    }
    setRealTimeTrackDrawing((prev) => new Set([...prev, memberName]));
    setRealTimeTrackData((prev) => ({
      ...prev,
      [memberName]: Array.isArray(prev[memberName]) ? prev[memberName] : []
    }));
    const sessionInfo = beginRealtimeTrackSession(memberName);
    const sessionId = sessionInfo.sessionId;
    const member = members.find((m) => m.name === memberName)
      || (membersRef.current || []).find((m) => m.name === memberName);
    if (member) {
      resumedDrawingRef.current.add(memberName);
      await startTrackDrawing(member, sessionId);
      if ((realTimeTrackDataRef.current[memberName] || []).length > 0) {
        setTimeout(() => drawTrackLine(memberName), 80);
      }
    } else {
      console.error('未找到队员:', memberName);
    }
  }, [members, membersRef, startTrackDrawing, drawTrackLine, isPastOfflineGrace]);

  const startReceivingTrackData = useCallback((memberName) => {
    dashboardDebug('开启接收轨迹数据:', memberName);
    setReceivingTrackData((prev) => new Set([...prev, memberName]));
    message.success(`已开启接收队员 ${memberName} 的轨迹数据`);
  }, []);

  /** 停止续画：保留已绘轨迹线与点位，不随刷新丢失 */
  const stopTrackFeatures = useCallback(async (memberName) => {
    console.log('停止轨迹续画（保留已绘数据）:', memberName);
    endTrackSession(memberName);
    resumedDrawingRef.current.delete(memberName);
    const member = (membersRef.current || members).find((m) => m.name === memberName);
    if (member?.positionWatcher) {
      clearInterval(member.positionWatcher);
      member.positionWatcher = null;
    }
    setReceivingTrackData((prev) => {
      const next = new Set(prev);
      next.delete(memberName);
      return next;
    });
    setRealTimeTrackDrawing((prev) => {
      const next = new Set(prev);
      next.delete(memberName);
      return next;
    });
    // 确保冻结的线仍在地图上
    if ((realTimeTrackDataRef.current[memberName] || []).length > 0) {
      setTimeout(() => drawTrackLine(memberName), 50);
    }
  }, [members, membersRef, drawTrackLine]);

  useEffect(() => { stopTrackFeaturesRef.current = stopTrackFeatures; }, [stopTrackFeatures]);

  /** 彻底清除某队员的实时绘制轨迹（点位 + 线 + 持久化） */
  const clearRealtimeTrack = useCallback((memberName) => {
    endTrackSession(memberName);
    resumedDrawingRef.current.delete(memberName);
    graceNotifiedRef.current.delete(memberName);
    const member = (membersRef.current || members).find((m) => m.name === memberName);
    if (member?.positionWatcher) {
      clearInterval(member.positionWatcher);
      member.positionWatcher = null;
    }
    setRealTimeTrackDrawing((prev) => {
      const next = new Set(prev);
      next.delete(memberName);
      return next;
    });
    setRealTimeTrackData((prev) => {
      const next = { ...prev };
      delete next[memberName];
      return next;
    });
    setShowRealtimeTracks((prev) => {
      if (!(memberName in prev)) return prev;
      const next = { ...prev };
      delete next[memberName];
      return next;
    });
    removeTrackDrawingLine(memberName);
    removePersistedMemberRealtimeTrack(memberName);
  }, [members, membersRef, removeTrackDrawingLine]);

  const cleanupTrackFeatures = useCallback(() => {
    // 仅清地图线与轮询；不写空持久化，避免刷新后轨迹丢失
    clearMemberPositionWatchers(members);
    Object.keys(trackDrawingLinesRef.current).forEach((memberName) => {
      removeTrackDrawingLine(memberName);
    });
    setTrackDrawingLines({});
  }, [members, removeTrackDrawingLine]);

  /** 用历史密点填充实时轨迹（任务记录轨迹：补洞，避免离线再上线硬拉直线） */
  const seedRealtimeTrackPoints = useCallback((memberName, points = []) => {
    const name = String(memberName || '').trim();
    if (!name || !Array.isArray(points) || !points.length) return;
    setRealTimeTrackData((prev) => {
      const existing = prev[name] || [];
      const byTs = new Map();
      existing.forEach((pt) => {
        const key = String(pt?.timestamp || pt?.recorded_at || `${pt?.lat},${pt?.lng}`);
        byTs.set(key, pt);
      });
      points.forEach((pt) => {
        if (!pt || !Number.isFinite(Number(pt.lat)) || !Number.isFinite(Number(pt.lng))) return;
        const key = String(pt?.timestamp || pt?.recorded_at || `${pt.lat},${pt.lng}`);
        byTs.set(key, pt);
      });
      const merged = Array.from(byTs.values()).sort((a, b) => {
        const ta = Date.parse(a.timestamp || a.recorded_at || 0) || 0;
        const tb = Date.parse(b.timestamp || b.recorded_at || 0) || 0;
        return ta - tb;
      });
      // 过滤原地抖动后再截断
      const cleaned = sanitizeTrackPoints(merged);
      const next = cleaned.length > 2000 ? cleaned.slice(-2000) : cleaned;
      return { ...prev, [name]: next };
    });
    setShowRealtimeTracks((prev) => ({ ...prev, [name]: true }));
    setTimeout(() => drawTrackLine(name), 60);
  }, [drawTrackLine]);

  /**
   * 任务「记录轨迹」：对给定队员名自动开启续画（已在画则只确保可见）
   */
  const ensureMissionRecordTrackDrawing = useCallback(async (memberNames = []) => {
    const names = (memberNames || []).map((n) => String(n || '').trim()).filter(Boolean);
    for (const name of names) {
      if (!name) continue;
      setShowRealtimeTracks((prev) => ({ ...prev, [name]: true }));
      // 离线超限停画后：任务记录轨迹不得立刻自动拉起（避免 toast 刷屏）
      if (graceNotifiedRef.current.has(name) || isPastOfflineGrace(name)) {
        if ((realTimeTrackDataRef.current[name] || []).length > 0) {
          setTimeout(() => drawTrackLine(name), 50);
        }
        continue;
      }
      if (!realTimeTrackDrawingRef.current.has(name)) {
        await startRealTimeTrackDrawing(name, { auto: true });
      } else if ((realTimeTrackDataRef.current[name] || []).length > 0) {
        setTimeout(() => drawTrackLine(name), 50);
      }
    }
  }, [startRealTimeTrackDrawing, drawTrackLine, isPastOfflineGrace]);

  /** 兼容旧调用名：不再全员自动开画；任务记录轨迹走 ensureMissionRecordTrackDrawing */
  const autoStartTrackDrawing = useCallback(() => {
    dashboardDebug('autoStartTrackDrawing: use ensureMissionRecordTrackDrawing for mission recordTracks');
  }, []);

  // 地图就绪后：恢复已保存的轨迹线
  useEffect(() => {
    if (restoredRef.current) return undefined;
    const timer = setInterval(() => {
      const map = getMap?.();
      if (!map || !map.isStyleLoaded?.()) return;
      restoredRef.current = true;
      clearInterval(timer);
      Object.keys(realTimeTrackDataRef.current || {}).forEach((name) => {
        if ((realTimeTrackDataRef.current[name] || []).length > 0) {
          drawTrackLine(name);
        }
      });
    }, 300);
    return () => clearInterval(timer);
  }, [getMap, drawTrackLine]);

  // 队员列表就绪后：对「仍在绘制中」的队员恢复续画 watcher（已超离线宽限则只保留线、不恢复 watcher）
  useEffect(() => {
    if (!realTimeTrackDrawing.size) return;
    const source = membersRef.current || members;
    if (!source?.length) return;
    const graceMin = Math.max(1, Number(offlineGraceMinutesRef.current) || 30);
    const graceMs = graceMin * 60 * 1000;
    const now = Date.now();
    const stoppedNames = [];
    realTimeTrackDrawing.forEach((name) => {
      if (resumedDrawingRef.current.has(name)) return;
      const member = source.find((m) => m.name === name);
      if (!member) return;
      const lastSeen = member?.last_seen ? new Date(member.last_seen).getTime() : NaN;
      if (Number.isFinite(lastSeen) && now - lastSeen >= graceMs) {
        // 持久化恢复时已超限：停画并记一次提示，避免与 ensure 来回拉起
        if (!graceNotifiedRef.current.has(name)) {
          graceNotifiedRef.current.add(name);
          stoppedNames.push(name);
        }
        stopTrackFeatures(name);
        return;
      }
      resumedDrawingRef.current.add(name);
      const sessionInfo = beginRealtimeTrackSession(name);
      startTrackDrawing(member, sessionInfo.sessionId);
      if ((realTimeTrackDataRef.current[name] || []).length > 0) {
        setTimeout(() => drawTrackLine(name), 80);
      }
      dashboardDebug('已恢复队员实时轨迹续画:', name);
    });
    if (stoppedNames.length === 1) {
      message.info(`${stoppedNames[0]} 已离线超过 ${graceMin} 分钟，已停止绘制（轨迹线条保留）`);
    } else if (stoppedNames.length > 1) {
      message.info(`${stoppedNames.length} 名队员已离线超过 ${graceMin} 分钟，已停止绘制（轨迹线条保留）`);
    }
  }, [members, membersRef, realTimeTrackDrawing, startTrackDrawing, drawTrackLine, stopTrackFeatures]);

  useEffect(() => {
    if (!(members.length > 0 && realTimeTrackDrawing.size > 0)) return;
    realTimeTrackDrawing.forEach((memberName) => {
      const member = members.find((m) => m.name === memberName);
      if (!(member && member.last_latitude && member.last_longitude)) return;
      setRealTimeTrackData((prev) => {
        const { nextState, added } = mergeRealtimeTrackPoint(prev, memberName, member);
        if (!added) return prev;
        setTimeout(() => drawTrackLine(memberName), 50);
        return nextState;
      });
    });
  }, [members, realTimeTrackDrawing, drawTrackLine]);

  // 持续离线超过 N 分钟：停止续画，保留已绘线条（不依赖 ONLINE_TIMEOUT）
  // 空依赖 + refs：队员列表轮询不得重置 interval / 重复 toast
  useEffect(() => {
    const tick = () => {
      const drawing = realTimeTrackDrawingRef.current;
      if (!drawing?.size) return;
      const graceMin = Math.max(1, Number(offlineGraceMinutesRef.current) || 30);
      const graceMs = graceMin * 60 * 1000;
      const now = Date.now();
      const source = membersRef.current || [];
      const toStop = [];
      drawing.forEach((name) => {
        const member = (source || []).find((m) => m.name === name);
        const lastSeen = member?.last_seen ? new Date(member.last_seen).getTime() : NaN;
        if (!Number.isFinite(lastSeen)) return;
        if (now - lastSeen < graceMs) {
          // 回到宽限内才清「本轮已提示」，允许下次真正离线再提示一次
          graceNotifiedRef.current.delete(name);
          return;
        }
        if (graceNotifiedRef.current.has(name)) return;
        toStop.push(name);
      });
      const stop = stopTrackFeaturesRef.current;
      toStop.forEach((name) => {
        graceNotifiedRef.current.add(name);
        if (typeof stop === 'function') stop(name);
      });
      if (toStop.length === 1) {
        message.info(`${toStop[0]} 已离线超过 ${graceMin} 分钟，已停止绘制（轨迹线条保留）`);
      } else if (toStop.length > 1) {
        message.info(`${toStop.length} 名队员已离线超过 ${graceMin} 分钟，已停止绘制（轨迹线条保留）`);
      }
    };
    tick();
    const timer = setInterval(tick, 15000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => () => {
    // 卸载前再落盘一次
    savePersistedRealtimeTracks({
      drawing: [...realTimeTrackDrawingRef.current],
      trackData: realTimeTrackDataRef.current
    });
    clearMemberPositionWatchers(membersRef.current || members);
  }, []);

  return {
    realTimeTrackDrawing,
    receivingTrackData,
    realTimeTrackData,
    showRealtimeTracks,
    trackDrawingLines,
    startReceivingTrackData,
    startRealTimeTrackDrawing,
    stopTrackFeatures,
    clearRealtimeTrack,
    hideRealtimeTrack,
    setRealtimeTrackVisible,
    autoStartTrackDrawing,
    seedRealtimeTrackPoints,
    ensureMissionRecordTrackDrawing,
    cleanupTrackFeatures,
    redrawRealtimeTrack: drawTrackLine
  };
}
